import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '../../lib/axios';

/**
 * Buscador de contactos con autocompletado.
 *
 * Reemplaza al <select> plano, que sólo cargaba los primeros 100 contactos y
 * obligaba a encontrar el correcto a ojo. El endpoint /api/contacts ya busca
 * por nombre, correo, teléfono y empresa, así que acá sólo hace falta
 * consultarlo con retardo y mostrar los resultados de forma legible.
 */
export default function ContactPicker({ value, initialContact = null, onChange, disabled = false }) {
    const [selected, setSelected] = useState(initialContact);
    const [term, setTerm] = useState('');
    const [debounced, setDebounced] = useState('');
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const boxRef = useRef(null);
    const inputRef = useRef(null);

    // El contacto puede llegar después (al abrir una cotización existente, la
    // consulta que lo trae resuelve más tarde que el primer render).
    useEffect(() => {
        if (initialContact && initialContact.id === value) setSelected(initialContact);
        if (!value) setSelected(null);
    }, [initialContact, value]);

    // Cuando sólo se recibe el id y no el contacto (por ejemplo al llegar desde
    // la ficha 360 con ?contact_id=), se resuelve solo. Sin esto quedaría el
    // estado ambiguo de tener un cliente asignado que el buscador no muestra.
    const needsResolve = Boolean(value) && !selected;
    const { data: resolvedResp } = useQuery({
        queryKey: ['contact', value],
        queryFn: () => axios.get(`/api/contacts/${value}`),
        enabled: needsResolve,
    });

    useEffect(() => {
        const c = resolvedResp?.data?.data ?? resolvedResp?.data;
        if (c?.id) setSelected(c);
    }, [resolvedResp]);

    // Retardo para no disparar una consulta por cada tecla.
    useEffect(() => {
        const t = setTimeout(() => setDebounced(term.trim()), 300);
        return () => clearTimeout(t);
    }, [term]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const { data: resp, isFetching } = useQuery({
        queryKey: ['contact-search', debounced],
        queryFn: () => axios.get('/api/contacts', { params: { search: debounced, per_page: 8 } }),
        enabled: open && debounced.length >= 2,
    });
    const results = resp?.data?.data || [];

    useEffect(() => setHighlighted(0), [results.length]);

    const pick = (contact) => {
        setSelected(contact);
        setTerm('');
        setOpen(false);
        onChange?.(contact?.id ?? '', contact);
    };

    const clear = () => {
        setSelected(null);
        setTerm('');
        onChange?.('', null);
        inputRef.current?.focus();
    };

    const onKeyDown = (e) => {
        if (!open || results.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => (h + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => (h - 1 + results.length) % results.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(results[highlighted]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    // Con un contacto elegido se muestra su ficha en vez del buscador: evita
    // el estado ambiguo de "hay uno seleccionado pero el campo dice otra cosa".
    if (selected) {
        return (
            <div className="flex items-center justify-between gap-2 w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800">
                <div className="min-w-0">
                    <p className="font-bold dark:text-white truncate">
                        {selected.full_name || selected.email || 'Contacto sin nombre'}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                        {[selected.email, selected.phone, selected.company].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                    </p>
                </div>
                {!disabled && (
                    <button type="button" onClick={clear} title="Quitar cliente"
                        className="shrink-0 text-gray-400 hover:text-red-500">
                        <span className="material-symbols-outlined text-lg">close</span>
                    </button>
                )}
            </div>
        );
    }

    return (
        <div ref={boxRef} className="relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none">
                search
            </span>
            <input
                ref={inputRef}
                type="text"
                value={term}
                disabled={disabled}
                onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder="Buscar por nombre, correo o teléfono"
                className="w-full text-sm pl-10 pr-3 p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white disabled:opacity-60"
            />

            {open && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-72 overflow-y-auto">
                    {term.trim().length < 2 ? (
                        <p className="px-3 py-3 text-xs text-gray-400">Escribí al menos 2 caracteres.</p>
                    ) : isFetching ? (
                        <p className="px-3 py-3 text-xs text-gray-400">Buscando...</p>
                    ) : results.length === 0 ? (
                        <p className="px-3 py-3 text-xs text-gray-400">Ningún contacto coincide con “{term.trim()}”.</p>
                    ) : (
                        results.map((c, i) => (
                            <button
                                key={c.id}
                                type="button"
                                onMouseEnter={() => setHighlighted(i)}
                                onClick={() => pick(c)}
                                className={`w-full text-left px-3 py-2.5 border-b last:border-b-0 border-gray-100 dark:border-gray-700/50 ${
                                    i === highlighted ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                }`}
                            >
                                <p className="text-sm font-bold dark:text-white truncate">
                                    {c.full_name || c.email || 'Sin nombre'}
                                </p>
                                <p className="text-xs text-gray-400 truncate">
                                    {[c.email, c.phone, c.company].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                                </p>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
